import { Button, PasswordInput } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import type { Gate } from '../../shared/gates';
import { failureMessage, unlockGate } from '../api';
import { gateKey, gateQuery } from '../queries';

const UNAVAILABLE = 'We could not check the password. Try again.';

/**
 * Shows `children` once this browser holds the gate's cookie; until then, the
 * password form inside `frame`. Register a new gate name in `shared/gates.ts`
 * and give the Worker its password in `gatePasswords`.
 */
export function PasswordGate({
  gate,
  accent,
  frame,
  children,
}: {
  gate: Gate;
  /** Mantine color for the button, such as `deep` on clinic pages. */
  accent?: string;
  frame: (content: ReactNode) => ReactNode;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const query = useQuery(gateQuery(gate));
  if (query.data?.unlocked) return children;
  if (query.isPending)
    return frame(
      <div
        aria-busy="true"
        className="bg-surface shadow-card mx-auto h-64 w-full max-w-md animate-pulse rounded-xl"
      />,
    );
  return frame(
    <PasswordForm
      gate={gate}
      accent={accent}
      onUnlocked={() =>
        queryClient.setQueryData(gateKey(gate), { unlocked: true })
      }
    />,
  );
}

export function PasswordForm({
  gate,
  accent,
  onUnlocked,
}: {
  gate: Gate;
  accent?: string;
  onUnlocked: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await unlockGate(gate, password);
      onUnlocked();
    } catch (failure) {
      setError(failureMessage(failure, UNAVAILABLE));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-surface shadow-card mx-auto flex w-full max-w-md flex-col gap-5 rounded-xl px-5 py-6"
    >
      <header className="flex flex-col gap-1.5">
        <h2 className="text-ink text-2xl font-semibold">Enter the password</h2>
        <p className="text-muted text-base">
          Enter the password FTLO gave you to open the application.
        </p>
      </header>
      <PasswordInput
        label="Password"
        value={password}
        onChange={(e) => {
          setPassword(e.currentTarget.value);
          setError('');
        }}
        autoComplete="off"
        error={error}
        // oxlint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <Button type="submit" color={accent} loading={busy} disabled={!password}>
        Continue
      </Button>
    </form>
  );
}
